import { useCallback, useState } from 'react';
import {
  LazadaStandaloneAuthResultBody,
  generateAuthUrl,
  standaloneResult,
} from 'services/api/admin';
import { useRequest } from 'ahooks';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useTransAppProductName } from 'hooks/useTrans';
import useOpenURL from './useOpenURL';
import posthog from '@leyan/lytics';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';

export interface AuthFormValues {
  shopType?: IM_API.Admin.StoreType;
  storeType?: IM_API.Admin.StoreType;
  region?: General.UpperCaseRegion;
}

export const useAuthShop = (externalType: General.PlatformUpperCase) => {
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const [formValues, setFormValues] = useState<AuthFormValues>();
  const [uuids, setUuids] = useState<string[]>([]);
  const [result, setResult] = useState<LazadaStandaloneAuthResultBody>();

  const openURL = useOpenURL();

  const {
    data: authUrl,
    loading,
    runAsync: getAuthURL,
  } = useRequest(
    async (departmentIds: number[]) => {
      if (formValues?.shopType || externalType === 'FACEBOOK' || externalType === 'JOLECT') {
        const { shopType, region } = formValues || {};
        const authURL = await generateAuthUrl({
          externalType,
          storeType: shopType,
          departmentIds,
          region,
        } as any);
        const uuid = new URL(authURL).searchParams.get('state');
        if (uuid) {
          setUuids((u) => [...u, uuid]);
          return authURL;
        } else {
          return Promise.reject(new Error('No uuid'));
        }
      }
    },
    {
      manual: true,
      onError() {
        message.error(t('authShop.获取授权链接失败'));
      },
    },
  );

  const copyURL = useCallback(
    async (departmentIds: number[], noPrefix = false) => {
      try {
        const authURL = await getAuthURL(departmentIds);
        if (authURL) {
          const template = noPrefix
            ? authURL
            : appProductNameT('authShop.copyLinkTemplate3', {
                platform: platformLabelMap[toLowerCase(externalType)],
              }) + authURL;
          await navigator.clipboard.writeText(template);
          message.success(t('authShop.复制成功'));
          return true;
        }
        throw new Error('请求失败');
      } catch (err) {
        message.error(t('authShop.复制链接失败'));
        return false;
      }
    },
    [getAuthURL, t, appProductNameT, externalType],
  );

  const openAuthUrl = useCallback(
    async (departmentIds: number[]) => {
      try {
        const authURL = await getAuthURL(departmentIds);
        if (authURL) {
          openURL(authURL, 'external');
          return true;
        }
        throw new Error('请求失败');
      } catch (err) {
        message.error(t('authShop.打开链接失败'));
        return false;
      }
    },
    [getAuthURL, openURL, t],
  );

  const checkAuthResult = useCallback(
    (uuid: string) => {
      return standaloneResult(uuid).catch((e) => {
        posthog.capture('$authReulstError', {
          platform: externalType,
          error: e,
        });
        return null;
      });
    },
    [externalType],
  );

  return {
    authUrl,
    uuids,
    setUuids,
    loading,
    formValues,
    setFormValues,
    copyURL,
    openAuthUrl,
    checkAuthResult,
    result,
    setResult,
  };
};
